/**
 * Fiche technique hébergement — Chambre Exécutive · Suite Jardin · Suite Lagune
 *
 * Route: /hebergement/:slug?check_in=...&check_out=...&adults=...
 *
 * Toute la fiche (nom, description, galerie, équipements, ordre des
 * pictogrammes) est chargée depuis la section CMS `hebergement_fiches`
 * (site_settings) et administrable via /staff/cms → « Hôtel / Hébergement ».
 * Aucune donnée n'est codée en dur ici — seul le mapping icône → composant
 * Lucide est statique (les admins choisissent l'icône par son nom).
 */
import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowRight, ChevronLeft, ChevronRight,
  Wifi, Snowflake, Tv, Wine, Lock, Bath, Sun, TreePine, Coffee,
  Users, Ruler, BedDouble, Phone, Package, Shirt, Sofa, ChefHat,
  Microwave, UtensilsCrossed, Droplets, DoorOpen, Waves, Sparkles,
  Palmtree, Star,
} from "lucide-react";
import { useSiteConfig, sel } from "../lib/site-config";
import { trackEvent } from "../lib/tracking";

// ── Icon registry ──────────────────────────────────────────────────
// Admins choose an icon by its name in the CMS. Extend this map to add
// new pictos.
const ICONS = {
  Wifi, Snowflake, Tv, Wine, Lock, Bath, Sun, TreePine, Coffee,
  Users, Ruler, BedDouble, Phone, Package, Shirt, Sofa, ChefHat,
  Microwave, UtensilsCrossed, Droplets, DoorOpen, Waves, Sparkles,
  Palmtree, Star,
};

export function iconComponent(name) {
  return ICONS[name] || Sparkles;
}

export default function HebergementDetail() {
  const { slug } = useParams();
  const [search] = useSearchParams();
  const cfg = useSiteConfig();
  const [idx, setIdx] = useState(0);

  const fiches = sel.hebergementFiches(cfg);
  const room = useMemo(() => {
    const list = fiches.rooms || [];
    // Legacy slug alias : /hebergement/chambre-exclusive → Chambre Exécutive
    const target = slug === "chambre-exclusive" ? "chambre-executive" : slug;
    return list.find((r) => r.slug === target);
  }, [fiches, slug]);

  useEffect(() => {
    if (room) trackEvent("hebergement_view", { slug });
    window.scrollTo(0, 0);
  }, [slug, room]);

  const bookingUrl = useMemo(() => {
    if (!room) return "#";
    const qs = new URLSearchParams();
    if (room.tier_id) qs.set("tier", room.tier_id);
    ["check_in", "check_out", "adults", "children"].forEach((k) => {
      const v = search.get(k);
      if (v) qs.set(k, v);
    });
    return `/booking/hebergement?${qs.toString()}`;
  }, [room, search]);

  // Amenities: resolve labels + icons through the shared catalog
  const amenities = useMemo(() => {
    if (!room) return [];
    const catalog = fiches.amenities_catalog || [];
    const byId = new Map(catalog.map((a) => [a.id, a]));
    const ids = [...(room.amenity_ids || [])];
    // has_private_garden ajoute automatiquement l'équipement s'il n'est pas déjà présent
    if (room.has_private_garden && !ids.includes("private_garden")) {
      ids.push("private_garden");
    }
    return ids
      .map((id) => byId.get(id))
      .filter(Boolean)
      .map((a) => ({ id: a.id, label: a.label_fr, Icon: iconComponent(a.icon) }));
  }, [room, fiches]);

  if (cfg === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white text-[#0A0A0A]/50 text-sm">
        Chargement de la fiche…
      </div>
    );
  }
  if (!room) return <Navigate to="/univers/hebergement" replace />;

  const images = room.images && room.images.length > 0 ? room.images : [
    "https://images.unsplash.com/photo-1611892440504-42a792e24d32?auto=format&fit=crop&w=1600&q=85",
  ];

  return (
    <div className="bg-white text-[#0A0A0A]" data-testid={`hebergement-detail-${slug}`}>
      {/* ─── Hero gallery ─── */}
      <section className="relative w-full h-[78vh] min-h-[560px] bg-[#0A0A0A]" data-testid="hebergement-gallery">
        <img
          key={idx}
          src={images[idx]}
          alt={room.name}
          className="absolute inset-0 w-full h-full object-cover animate-fadein"
        />
        <div className="absolute inset-0 bg-black/40" />

        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center px-6 text-center text-white pointer-events-none">
          {room.tagline && (
            <div className="text-[0.58rem] md:text-[0.62rem] tracking-[0.55em] uppercase text-white/85 mb-3 md:mb-4">
              · Hébergement · {room.tagline} ·
            </div>
          )}
          <h1 className="font-serif font-light text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.02]">
            {room.name}
          </h1>

          <Link
            to={bookingUrl}
            onClick={() => trackEvent("hebergement_book_click", { slug, from: "hero_mobile" })}
            className="md:hidden pointer-events-auto mt-6 inline-flex items-center gap-2 bg-[#B8922A] hover:bg-[#D4B256] text-white px-6 py-3 text-[0.65rem] tracking-[0.35em] uppercase transition-colors"
            data-testid="hebergement-book-cta-hero"
          >
            Réserver
            <ArrowRight size={12} strokeWidth={1.5} />
          </Link>

          <div className="mt-6 md:mt-7 flex items-center justify-center gap-2.5 pointer-events-auto" role="tablist" aria-label="Galerie photos">
            {images.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setIdx(i)}
                aria-label={`Photo ${i + 1}`}
                aria-selected={i === idx}
                className={`h-[3px] transition-all duration-500 ${
                  i === idx ? "w-10 bg-[#D4B256]" : "w-5 bg-white/45 hover:bg-white/75"
                }`}
                data-testid={`gallery-dot-${i}`}
              />
            ))}
          </div>
        </div>

        {images.length > 1 && (
          <>
            <button
              type="button"
              onClick={() => setIdx((idx + images.length - 1) % images.length)}
              className="hidden md:flex absolute top-1/2 left-4 md:left-8 -translate-y-1/2 w-11 h-11 md:w-13 md:h-13 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur border border-white/30 text-white transition-colors items-center justify-center"
              aria-label="Photo précédente"
              data-testid="gallery-prev"
            >
              <ChevronLeft size={22} strokeWidth={1.5} />
            </button>
            <button
              type="button"
              onClick={() => setIdx((idx + 1) % images.length)}
              className="hidden md:flex absolute top-1/2 right-4 md:right-8 -translate-y-1/2 w-11 h-11 md:w-13 md:h-13 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur border border-white/30 text-white transition-colors items-center justify-center"
              aria-label="Photo suivante"
              data-testid="gallery-next"
            >
              <ChevronRight size={22} strokeWidth={1.5} />
            </button>

            <div className="md:hidden absolute inset-x-0 bottom-16 z-10 flex items-center justify-center gap-4">
              <button
                type="button"
                onClick={() => setIdx((idx + images.length - 1) % images.length)}
                className="w-11 h-11 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur border border-white/30 text-white flex items-center justify-center"
                aria-label="Photo précédente"
                data-testid="gallery-prev-mobile"
              >
                <ChevronLeft size={20} strokeWidth={1.5} />
              </button>
              <button
                type="button"
                onClick={() => setIdx((idx + 1) % images.length)}
                className="w-11 h-11 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur border border-white/30 text-white flex items-center justify-center"
                aria-label="Photo suivante"
                data-testid="gallery-next-mobile"
              >
                <ChevronRight size={20} strokeWidth={1.5} />
              </button>
            </div>
          </>
        )}

        <div className="absolute bottom-6 left-6 md:bottom-10 md:left-10 z-10 text-[0.62rem] tracking-[0.35em] uppercase text-white/70">
          {String(idx + 1).padStart(2, "0")} / {String(images.length).padStart(2, "0")}
          {(room.size || room.bed || room.view) && (
            <span className="hidden md:inline">
              {room.size && ` · ${room.size}`}
              {room.bed && ` · ${room.bed}`}
              {room.view && ` · Vue ${room.view}`}
            </span>
          )}
        </div>
      </section>

      {/* ─── Description ─── */}
      {(room.description || room.experience) && (
        <section className="py-20 md:py-28 bg-white" data-testid="hebergement-description">
          <div className="max-w-4xl mx-auto px-6">
            <div className="text-[0.6rem] tracking-[0.45em] uppercase text-[#B8922A] mb-6">
              · Présentation ·
            </div>
            <h2 className="font-serif font-light text-3xl md:text-4xl leading-tight mb-10">
              L&apos;expérience {room.name}
            </h2>
            {room.description && (
              <p className="text-base md:text-lg leading-[1.9] text-[#0A0A0A]/75 font-light mb-6">
                {room.description}
              </p>
            )}
            {room.experience && (
              <p className="text-base md:text-lg leading-[1.9] text-[#0A0A0A]/70 font-light italic">
                {room.experience}
              </p>
            )}
          </div>
        </section>
      )}

      {/* ─── Fiche technique · Équipements & Commodités ─── */}
      <section className="py-16 md:py-24 bg-[#FAF7F2] border-y border-[#0A0A0A]/8" data-testid="hebergement-amenities">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12 md:mb-16">
            <div className="text-[0.6rem] tracking-[0.45em] uppercase text-[#B8922A] mb-4">
              · Fiche technique ·
            </div>
            <h2 className="font-serif font-light text-3xl md:text-4xl leading-tight">
              Équipements &amp; commodités
            </h2>
          </div>

          {/* Key stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 mb-14">
            {room.size && <StatBox icon={Ruler} label="Surface" value={room.size} />}
            {room.bed && <StatBox icon={BedDouble} label="Lit" value={room.bed} />}
            {room.view && <StatBox icon={Sun} label="Vue" value={room.view} />}
            {room.capacity && <StatBox icon={Users} label="Capacité" value={`${room.capacity} pers.`} />}
          </div>

          {/* Amenities grid */}
          {amenities.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-6" data-testid="amenities-grid">
              {amenities.map((a) => (
                <div key={a.id} className="flex items-center gap-4" data-testid={`amenity-${a.id}`}>
                  <div className="flex-shrink-0 w-11 h-11 rounded-full bg-white border border-[#0A0A0A]/10 flex items-center justify-center text-[#B8922A]">
                    <a.Icon size={18} strokeWidth={1.5} />
                  </div>
                  <span className="text-sm text-[#0A0A0A]/80 font-light">{a.label}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ─── Booking CTA band ─── */}
      <section className="py-20 md:py-24 bg-[#0A0A0A] text-white text-center" data-testid="hebergement-book-band">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-[0.6rem] tracking-[0.55em] uppercase text-[#D4B256] mb-6">
            · Réservation ·
          </div>
          <h2 className="font-serif font-light text-3xl md:text-4xl leading-tight mb-8">
            Prêt à vivre l&apos;expérience {room.name} ?
          </h2>
          {search.get("check_in") && search.get("check_out") && (
            <p className="text-sm text-white/60 mb-8">
              Vos dates &amp; voyageurs sont conservés — un seul clic pour continuer.
            </p>
          )}
          <Link
            to={bookingUrl}
            onClick={() => trackEvent("hebergement_book_click", { slug })}
            className="inline-flex items-center gap-3 bg-[#B8922A] hover:bg-[#D4B256] text-white px-10 py-4 text-[0.7rem] tracking-[0.35em] uppercase transition-colors"
            data-testid="hebergement-book-cta"
          >
            Réserver cette chambre
            <ArrowRight size={14} strokeWidth={1.5} />
          </Link>
          <div className="mt-8 text-[0.65rem] tracking-[0.3em] uppercase text-white/40">
            <Link to="/univers/hebergement" className="hover:text-white transition-colors">
              ← Voir tous les hébergements
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function StatBox({ icon: Icon, label, value }) {
  return (
    <div className="text-center">
      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-white border border-[#0A0A0A]/10 text-[#B8922A] mb-3">
        <Icon size={18} strokeWidth={1.5} />
      </div>
      <div className="text-[0.55rem] tracking-[0.4em] uppercase text-[#0A0A0A]/50 mb-1">{label}</div>
      <div className="font-serif text-lg text-[#0A0A0A]">{value}</div>
    </div>
  );
}
